import * as z from "zod/v4";

export const ProductInsightCitationSchema = z.object({
  returnId: z.string().describe("Return ID supporting this insight"),
  productId: z.string().describe("Product ID associated with this citation"),
});

export type ProductInsightCitation = z.infer<
  typeof ProductInsightCitationSchema
>;

export const ProductInsightReferenceSchema = z.object({
  productId: z.string().describe("Product ID of the insight being referenced"),
  insightIndex: z
    .number()
    .min(0)
    .describe("Index of the insight within that product's insights array"),
});

export type ProductInsightReference = z.infer<
  typeof ProductInsightReferenceSchema
>;

export const BaseInsightSchema = z.object({
  title: z.string().describe("A clear, concise title for the insight"),
  description: z
    .string()
    .describe(
      "A detailed description of the insight, explaining the pattern, root cause, and impact",
    ),
  citations: z
    .array(ProductInsightCitationSchema)
    .describe("Return IDs supporting this insight")
    .default([]),
  confidenceScore: z
    .number()
    .min(1)
    .max(10)
    .describe(
      "Confidence score from 1 (low confidence) to 10 (high confidence)",
    ),
});

export type BaseInsight = z.infer<typeof BaseInsightSchema>;

export const ProductInsightSchema = BaseInsightSchema.extend({
  productId: z.string().describe("The product ID this insight is about"),
});

export type ProductInsight = z.infer<typeof ProductInsightSchema>;

export const HighLevelProductInsightSchema = BaseInsightSchema;

export type HighLevelProductInsight = z.infer<
  typeof HighLevelProductInsightSchema
>;

export const ProductReturnInsightsStateSchema = z.object({
  productId: z.string().describe("The product ID to analyze"),
  productName: z.string().describe("The product name"),
  insights: z
    .array(ProductInsightSchema)
    .default([])
    .describe("Accumulated product insights"),
});

export type ProductReturnInsightsState = z.infer<
  typeof ProductReturnInsightsStateSchema
>;

export const AiProductInsightSchema_ = z.object({
  teamId: z.string().describe("The team ID"),
  insightRange: z.object({ start: z.date(), end: z.date() }),
  productInsights: z
    .array(ProductInsightSchema)
    .default([])
    .describe("All product-level insights from individual product agents"),
  highLevelProductInsights: z
    .array(HighLevelProductInsightSchema)
    .default([])
    .describe("High-level cross-product insights"),
  createdAt: z.date().optional(),
});

export const AiProductInsightSchema: z.ZodType<AiProductInsight> =
  AiProductInsightSchema_;
export interface AiProductInsight
  extends z.infer<typeof AiProductInsightSchema_> {}

// Agent input schema uses string dates for initialization
export const HighLevelProductInsightsInputSchema = z.object({
  teamId: z.string().describe("The team ID"),
  startDate: z.string().describe("Start date for analysis (YYYY-MM-DD)"),
  endDate: z.string().describe("End date for analysis (YYYY-MM-DD)"),
});

export type HighLevelProductInsightsInput = z.infer<
  typeof HighLevelProductInsightsInputSchema
>;

// Agent state schema is the same as the stored document schema
export const HighLevelProductInsightsStateSchema = AiProductInsightSchema_;

export type HighLevelProductInsightsState = z.infer<
  typeof HighLevelProductInsightsStateSchema
>;
